import * as Sentry from "@sentry/react";
import React from "react";

/**
 * Sentry 설정 상수
 */
const SENTRY_CONFIG = {
  DSN: "https://fd4b6458120b699fb189e37cba8bdd9d@o4509998485995520.ingest.de.sentry.io/4509998487306320",
  ENVIRONMENT: import.meta.env.VITE_APP_ENV || import.meta.env.MODE || "development",
  RELEASE: `wholsee-trek-adventures@${import.meta.env.VITE_APP_VERSION || "1.0.0"}`,
  SAMPLE_RATE: import.meta.env.PROD ? 1.0 : 0.1, // 프로덕션에서는 100%, 개발에서는 10%
  TRACES_SAMPLE_RATE: import.meta.env.PROD ? 0.1 : 0.01, // 성능 모니터링 샘플링 (개발: 1%)
  REPLAY_SAMPLE_RATE: import.meta.env.PROD ? 0.1 : 0.01, // 세션 리플레이 샘플링 (개발: 1%)
  PROFILE_SAMPLE_RATE: import.meta.env.PROD ? 0.1 : 0.01, // 프로파일링 샘플링 (개발: 1%)
} as const;

/**
 * Sentry 초기화 함수
 */
export const initSentry = (): void => {
  // 개발 환경에서 Sentry 비활성화 옵션
  if (import.meta.env.VITE_DISABLE_SENTRY === "true") {
    console.log("Sentry가 비활성화되었습니다.");
    return;
  }

  Sentry.init({
    dsn: SENTRY_CONFIG.DSN,
    environment: SENTRY_CONFIG.ENVIRONMENT,
    release: SENTRY_CONFIG.RELEASE,
    
    // 기본 PII 전송 설정
    sendDefaultPii: true,
    
    // 에러 샘플링 비율
    sampleRate: SENTRY_CONFIG.SAMPLE_RATE,
    
    // 성능 모니터링 설정
    tracesSampleRate: SENTRY_CONFIG.TRACES_SAMPLE_RATE,
    
    // 세션 리플레이 설정
    replaysSessionSampleRate: SENTRY_CONFIG.REPLAY_SAMPLE_RATE,
    replaysOnErrorSampleRate: 1.0, // 에러 발생 시 항상 리플레이 수집
    
    // 프로파일링 설정
    profilesSampleRate: SENTRY_CONFIG.PROFILE_SAMPLE_RATE,
    
    // React Router 통합 및 기타 통합 설정
    integrations: [
      // 브라우저 추적 통합 (최신 Sentry/React 내장)
      Sentry.browserTracingIntegration(),
      // 세션 리플레이 통합 (사용자 상호작용 기록)
      Sentry.replayIntegration({
        maskAllInputs: true, // 모든 입력 필드 마스킹
        maskAllText: false, // 텍스트는 마스킹하지 않음
        blockAllMedia: true, // 미디어 파일 블록
        networkDetailAllowUrls: [
          // 네트워크 요청 상세 정보 허용 URL
          /^\/api\//, 
          /supabase\.co/,
          /toss\.im/
        ],
      }),
      // 사용자 피드백 통합
      Sentry.feedbackIntegration({
        colorScheme: "system",
        // autoInject를 false로 변경하여 플로팅 버튼 비활성화
        autoInject: false,
        // 플로팅 버튼 위치 조정 (우측 하단에 ErrorButton이 있으므로 좌측으로)
        buttonLabel: "피드백",
        cancelButtonLabel: "취소",
        submitButtonLabel: "피드백 보내기",
        formTitle: "버그 신고 또는 피드백",
        nameLabel: "이름 (선택사항)",
        nameInputPlaceholder: "홀씨 사용자",
        emailLabel: "이메일 (선택사항)", 
        emailInputPlaceholder: "your.email@example.com",
        messageLabel: "무엇을 도와드릴까요?",
        messageInputPlaceholder: "문제가 발생했거나 개선 사항이 있으시면 알려주세요...",
        successMessageText: "피드백을 보내주셔서 감사합니다!",
      }),
    ],
    
    // 에러 필터링 - 무시할 에러들
    beforeSend(event) {
      // 개발 환경에서는 콘솔에도 출력
      if (import.meta.env.DEV) {
        console.error("Sentry Error:", event);
      }
      
      // 스크립트 로딩 에러 등 무시할 에러들
      if (event.exception) {
        const error = event.exception.values?.[0];
        if (error?.type === "ChunkLoadError" || 
            error?.value?.includes("Loading chunk") ||
            error?.value?.includes("Loading CSS chunk")) {
          return null; // 청크 로딩 에러는 무시
        }
      }
      
      return event;
    },
    
    // 성능 데이터 필터링
    beforeSendTransaction(event) {
      // 개발 환경에서 Rate Limit 방지를 위해 일부 트랜잭션만 전송
      if (import.meta.env.DEV) {
        // 디버그 로깅 비활성화 (Rate Limit 방지)
        // console.log("Sentry Transaction:", event);

        // 개발 환경에서는 중요한 트랜잭션만 샘플링
        const shouldSend = Math.random() < 0.05; // 5% 확률로 전송
        if (!shouldSend) {
          return null;
        }
      }

      return event;
    },
    
    // 추가 설정
    attachStacktrace: true, // 모든 메시지에 스택 트레이스 첨부
    autoSessionTracking: true, // 세션 자동 추적
    
    // Debug 모드 비활성화 (콘솔 로그 줄이기)
    debug: false,
    
    // 태그 설정
    initialScope: {
      tags: {
        component: "frontend",
        framework: "react",
        buildTool: "vite",
      },
    },
  });

  // 글로벌 에러 핸들러 설정
  window.addEventListener("unhandledrejection", (event) => {
    console.error("Unhandled Promise Rejection:", event.reason);
    Sentry.captureException(event.reason);
  });

  console.log(`Sentry 초기화 완료 (환경: ${SENTRY_CONFIG.ENVIRONMENT})`);
};

/**
 * 사용자 컨텍스트 설정
 */
export const setSentryUser = (user: {
  id: string;
  email?: string;
  role?: string;
  name?: string;
}): void => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
    extra: {
      role: user.role,
    },
  });
};

/**
 * 사용자 컨텍스트 제거
 */
export const clearSentryUser = (): void => {
  Sentry.setUser(null);
};

/**
 * 커스텀 태그 설정
 */
export const setSentryTag = (key: string, value: string): void => {
  Sentry.setTag(key, value);
};

/**
 * 커스텀 컨텍스트 설정
 */
export const setSentryContext = (key: string, context: Record<string, any>): void => {
  Sentry.setContext(key, context);
};

/**
 * 수동 에러 보고
 */
export const captureError = (error: Error, context?: Record<string, any>): string => {
  if (context) {
    return Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
      return Sentry.captureException(error);
    });
  }
  return Sentry.captureException(error);
};

/**
 * 메시지 로깅
 */
export const captureMessage = (
  message: string, 
  level: Sentry.SeverityLevel = "info",
  context?: Record<string, any>
): string => {
  if (context) {
    return Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
      return Sentry.captureMessage(message, level);
    });
  }
  return Sentry.captureMessage(message, level);
};

/**
 * 성능 모니터링을 위한 스팬 생성 (최신 Sentry API)
 */
export const startSpan = <T>(options: { name: string; op: string }, callback: () => T): T => {
  return Sentry.startSpan(options, callback);
};

/**
 * 브레드크럼 추가
 */
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb): void => {
  Sentry.addBreadcrumb(breadcrumb);
};

/**
 * Sentry 에러 바운더리용 HOC
 */
export const withSentryErrorBoundary = Sentry.withErrorBoundary;

/**
 * Sentry 프로파일러용 HOC (성능 모니터링)
 */
export const withSentryProfiling = Sentry.withProfiler;

/**
 * 사용자 피드백 위젯 표시
 */
export const showFeedbackDialog = (): void => {
  const feedbackIntegration = Sentry.getClient()?.getIntegrationByName?.("Feedback");
  if (feedbackIntegration && "openDialog" in feedbackIntegration) {
    (feedbackIntegration as any).openDialog();
  } else {
    console.warn("Feedback integration not found or not available");
  }
};

/**
 * 커스텀 에러 트래킹 헬퍼 함수들
 */
export const trackError = {
  /**
   * 결제 관련 에러 추적
   */
  payment: (error: Error, paymentData?: Record<string, any>) => {
    return captureError(error, {
      payment: {
        ...paymentData,
        timestamp: new Date().toISOString(),
      },
    });
  },

  /**
   * API 호출 에러 추적
   */
  api: (error: Error, apiInfo: { endpoint: string; method: string; statusCode?: number }) => {
    return captureError(error, {
      api: {
        ...apiInfo,
        timestamp: new Date().toISOString(),
      },
    });
  },

  /**
   * 사용자 인증 에러 추적
   */
  auth: (error: Error, authContext?: Record<string, any>) => {
    return captureError(error, {
      auth: {
        ...authContext,
        timestamp: new Date().toISOString(),
      },
    });
  },

  /**
   * 예약 관련 에러 추적
   */
  booking: (error: Error, bookingData?: Record<string, any>) => {
    return captureError(error, {
      booking: {
        ...bookingData,
        timestamp: new Date().toISOString(),
      },
    });
  },
};

/**
 * 성능 모니터링 헬퍼 함수들
 */
export const trackPerformance = {
  /**
   * 페이지 로드 성능 측정
   */
  pageLoad: (pageName: string) => {
    return Sentry.startSpan(
      {
        name: `Page Load: ${pageName}`,
        op: "navigation",
      },
      () => {
        // 페이지 로드 성능 측정 로직
        const navigationEntry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming;
        if (navigationEntry) {
          Sentry.setMeasurement("page_load_time", navigationEntry.loadEventEnd - navigationEntry.fetchStart, "millisecond");
          Sentry.setMeasurement("dom_content_loaded", navigationEntry.domContentLoadedEventEnd - navigationEntry.fetchStart, "millisecond");
        }
      }
    );
  },

  /**
   * API 호출 성능 측정
   */
  apiCall: async <T>(apiName: string, apiCall: () => Promise<T>): Promise<T> => {
    return await Sentry.startSpan(
      {
        name: `API Call: ${apiName}`,
        op: "http.client",
      },
      async () => {
        const startTime = performance.now();
        try {
          const result = await apiCall();
          const duration = performance.now() - startTime;
          Sentry.setMeasurement(`api_call_${apiName.toLowerCase().replace(/\s+/g, "_")}`, duration, "millisecond");
          return result;
        } catch (error) {
          const duration = performance.now() - startTime;
          Sentry.setMeasurement(`api_call_${apiName.toLowerCase().replace(/\s+/g, "_")}_failed`, duration, "millisecond");
          throw error;
        }
      }
    );
  },

  /**
   * 컴포넌트 렌더링 성능 측정
   */
  componentRender: (componentName: string, renderFn: () => void) => {
    return Sentry.startSpan(
      {
        name: `Component Render: ${componentName}`,
        op: "react.render",
      },
      renderFn
    );
  },
};

/**
 * React Error Boundary와 함께 사용할 에러 핸들러
 */
export const handleReactError = (error: Error, errorInfo: { componentStack: string }) => {
  Sentry.withScope((scope) => {
    scope.setTag("errorBoundary", true);
    scope.setContext("errorInfo", errorInfo);
    scope.setLevel("error");
    Sentry.captureException(error);
  });
};

export { Sentry };